/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import { Box, styled } from '@mui/system';
import { Typography } from '@mui/material';

function LinkedStepper({ linkedSteps, activeStepId, theme }) {
  const ecommerceSteps = useMemo(
    () => (linkedSteps || []).filter((s) => s.owner === 'ecommerce'),
    [linkedSteps],
  );
  const rsvpSteps = useMemo(
    () => (linkedSteps || []).filter((s) => s.owner !== 'ecommerce'),
    [linkedSteps],
  );

  const rsvpActiveIndex = rsvpSteps.findIndex((s) => s.id === activeStepId);
  const rsvpProgress = useMemo(() => {
    if (rsvpSteps.length > 1) return rsvpActiveIndex / (rsvpSteps.length - 1);
    return rsvpActiveIndex > -1 ? 1 : 0;
  }, [rsvpSteps, rsvpActiveIndex]);

  const primaryColor = theme?.vars?.palette?.primary?.main || theme?.palette?.primary?.main || '#1976d2';
  const kaufColor = theme?.palette?.text?.primary || '#1a1a1a';

  return (
    <PhaseBarWrapper>
      <PhaseSegment>
        <PhaseHeader>
          <PhaseLabel>
            <PhaseCheck aria-hidden="true">✓</PhaseCheck>
            Kauf
          </PhaseLabel>
          <PhaseHint>Abgeschlossen</PhaseHint>
        </PhaseHeader>
        <PhaseTrack>
          <PhaseFill sx={{ width: '100%', bgcolor: kaufColor }} />
        </PhaseTrack>
        <DotRow>
          {ecommerceSteps.map((step, idx) => (
            <DotItem key={step.id}>
              {idx > 0 && <DotConnector done sx={{ bgcolor: kaufColor }} />}
              <Dot
                done
                current={false}
                sx={{
                  bgcolor: kaufColor,
                  color: '#fff',
                  borderColor: kaufColor,
                }}
              >
                ✓
              </Dot>
              <DotLabel sx={{ color: 'text.disabled', fontWeight: 400 }}>
                {step.label}
              </DotLabel>
            </DotItem>
          ))}
        </DotRow>
      </PhaseSegment>

      <PhaseDivider />

      <PhaseSegment>
        <PhaseHeader>
          <PhaseLabel>Anmeldung</PhaseLabel>
          <PhaseHint>
            {rsvpActiveIndex > -1
              ? `Schritt ${rsvpActiveIndex + 1} von ${rsvpSteps.length}`
              : `${rsvpSteps.length} Schritte`}
          </PhaseHint>
        </PhaseHeader>
        <PhaseTrack>
          <PhaseFill sx={{ width: `${Math.round(rsvpProgress * 100)}%`, bgcolor: primaryColor }} />
        </PhaseTrack>
        <DotRow>
          {rsvpSteps.map((step, idx) => {
            const done = idx < rsvpActiveIndex;
            const current = idx === rsvpActiveIndex;
            return (
              <DotItem key={step.id}>
                {idx > 0 && <DotConnector done={done} sx={{ bgcolor: done ? primaryColor : 'divider' }} />}
                <Dot
                  done={done}
                  current={current}
                  sx={{
                    bgcolor: done ? primaryColor : 'transparent',
                    color: done ? '#fff' : current ? primaryColor : 'text.disabled',
                    borderColor: done ? primaryColor : current ? primaryColor : 'divider',
                  }}
                >
                  {done ? '✓' : idx + 1}
                </Dot>
                <DotLabel sx={{ color: current ? 'text.primary' : 'text.disabled', fontWeight: current ? 500 : 400 }}>
                  {step.label}
                </DotLabel>
              </DotItem>
            );
          })}
        </DotRow>
      </PhaseSegment>
    </PhaseBarWrapper>
  );
}

const PhaseBarWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const PhaseSegment = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1.5, 1.5, 1.25),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.25, 2, 1.5),
  },
}));

const PhaseDivider = styled(Box)(({ theme }) => ({
  height: '1px',
  width: '100%',
  background: theme.palette.divider,
  flexShrink: 0,
  [theme.breakpoints.up('sm')]: {
    height: 'auto',
    width: '1px',
    alignSelf: 'stretch',
  },
}));

const PhaseHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px',
});

const PhaseLabel = styled(Typography)({
  fontSize: '11px',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  color: 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
});

const PhaseCheck = styled('span')(({ theme }) => ({
  fontSize: '11px',
  color: theme.palette.success.main,
  fontWeight: 700,
}));

const PhaseHint = styled(Typography)({
  fontSize: '12px',
  color: 'text.secondary',
});

const PhaseTrack = styled(Box)(({ theme }) => ({
  height: '3px',
  borderRadius: '2px',
  backgroundColor: theme.palette.divider,
  overflow: 'hidden',
  marginBottom: '12px',
}));

const PhaseFill = styled(Box)({
  height: '100%',
  borderRadius: '2px',
  transition: 'width 0.3s ease',
});

const DotRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  position: 'relative',
});

const DotItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
  position: 'relative',
  minWidth: 0,
});

const DotConnector = styled(Box)({
  position: 'absolute',
  top: '11px',
  right: '50%',
  left: '-50%',
  height: '1px',
  transition: 'background-color 0.2s',
});

const Dot = styled(Box)({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '11px',
  fontWeight: 500,
  flexShrink: 0,
  zIndex: 1,
  border: '1.5px solid',
  transition: 'all 0.2s',
});

const DotLabel = styled(Typography)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
    fontSize: '11px',
    textAlign: 'center',
    marginTop: '5px',
    lineHeight: 1.3,
    maxWidth: '72px',
    wordBreak: 'break-word',
    hyphens: 'auto',
  },
}));

export default LinkedStepper;
